/**
 * Keystone RFP Agent entry point
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { App, initializeAPIClient, getApiBaseUrl, useAuthStore } from '@tracekit/core';
import { keystoneConfig } from '../../../../config/keystone.config';
import { rfpAgentPlugin } from '@tracekit/plugin-rfp-agent';
import './index.css';

// Initialize API client
const apiUrl = getApiBaseUrl();
const client = initializeAPIClient(apiUrl);

// Clear workspace localStorage if tenant/project ID doesn't match config
// This ensures environment variables (VITE_TENANT_ID, VITE_PROJECT_ID) take precedence
if (import.meta.env.DEV) {
  try {
    const workspaceData = localStorage.getItem('tracekit-workspace');
    if (workspaceData) {
      const parsed = JSON.parse(workspaceData);
      const persistedTenantId = parsed?.state?.tenantId;
      const persistedProjectId = parsed?.state?.projectId;
      const expectedTenantId = keystoneConfig.customization?.defaultTenantId;
      const expectedProjectId = keystoneConfig.customization?.defaultProjectId;
      
      // Only enforce defaults if environment variables are explicitly set
      const envTenantId = import.meta.env.VITE_TENANT_ID;
      const envProjectId = import.meta.env.VITE_PROJECT_ID;
      
      let shouldClear = false;
      const reasons: string[] = [];
      
      // If env variables are set, they take precedence - clear if mismatch
      if (envTenantId && persistedTenantId && persistedTenantId !== expectedTenantId) {
        shouldClear = true;
        reasons.push(`Tenant ID mismatch (persisted: ${persistedTenantId}, expected: ${expectedTenantId})`);
      }
      
      if (envProjectId && persistedProjectId && persistedProjectId !== expectedProjectId) {
        shouldClear = true;
        reasons.push(`Project ID mismatch (persisted: ${persistedProjectId}, expected: ${expectedProjectId})`);
      }
      
      if (shouldClear) {
        console.log('[Main] Clearing workspace localStorage:', reasons.join(', '));
        localStorage.removeItem('tracekit-workspace');
      } else {
        console.log('[Main] Workspace localStorage valid:', {
          persistedTenantId,
          persistedProjectId,
          expectedTenantId,
          expectedProjectId
        });
      }
    }
  } catch (e) {
    console.warn('[Main] Error checking workspace localStorage:', e);
  }
}

// For development: Auto-set master key if available from env or localStorage
if (import.meta.env.DEV) {
  // Check for master key in environment variable (VITE_MASTER_API_KEY)
  const envMasterKey = import.meta.env.VITE_MASTER_API_KEY;
  if (envMasterKey) {
    console.log('[Main] Setting master API key from environment variable:', envMasterKey.substring(0, 20) + '...');
    client.setApiKey(envMasterKey);
    // Also set in auth store for persistence
    try {
      useAuthStore.getState().setApiKey(envMasterKey);
      console.log('[Main] API key also set in auth store');
    } catch (err) {
      console.warn('[Main] Failed to set API key in auth store:', err);
    }
  } else {
    console.warn('[Main] VITE_MASTER_API_KEY not found in environment variables');
    // Check localStorage for persisted API key
    try {
      const authData = localStorage.getItem('tracekit-auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        if (parsed.state?.apiKey) {
          console.log('[Main] Restoring API key from localStorage');
          client.setApiKey(parsed.state.apiKey);
        } else if (parsed.state?.accessToken) {
          console.log('[Main] Restoring access token from localStorage');
          client.setAccessToken(parsed.state.accessToken);
        } else {
          console.warn('[Main] No API key or access token found in localStorage');
        }
      } else {
        console.warn('[Main] No auth data in localStorage');
      }
    } catch (e) {
      console.error('[Main] Error reading localStorage:', e);
    }
  }
  
  // Log final auth state
  console.log('[Main] API client auth state:', {
    hasAuth: (client as any).hasAuth ? (client as any).hasAuth() : 'unknown',
    apiKeyLength: (client as any).apiKey ? (client as any).apiKey.length : 0,
  });
}

// Override tenant and project IDs from environment variables if provided
// This allows launch.json configurations to take precedence
if (import.meta.env.VITE_TENANT_ID) {
  console.log('[Main] Overriding tenant ID from environment:', import.meta.env.VITE_TENANT_ID);
  if (keystoneConfig.customization) {
    keystoneConfig.customization.defaultTenantId = import.meta.env.VITE_TENANT_ID;
  }
}

if (import.meta.env.VITE_PROJECT_ID) {
  console.log('[Main] Overriding project ID from environment:', import.meta.env.VITE_PROJECT_ID);
  if (keystoneConfig.customization) {
    keystoneConfig.customization.defaultProjectId = import.meta.env.VITE_PROJECT_ID;
  }
}

console.log('╔══════════════════════════════════════════════════════════════╗');
console.log('║  Keystone RFP Agent - TraceKit Initialized                  ║');
console.log('╠══════════════════════════════════════════════════════════════╣');
console.log('║  API URL:', apiUrl.padEnd(49), '║');
console.log('║  Frontend:', (typeof window !== 'undefined' ? window.location.origin : 'N/A').padEnd(49), '║');
console.log('║  Tenant ID:', (keystoneConfig.customization?.defaultTenantId || 'N/A').padEnd(47), '║');
console.log('║  Project ID:', (keystoneConfig.customization?.defaultProjectId || 'N/A').padEnd(46), '║');
console.log('╚══════════════════════════════════════════════════════════════╝');

// Entity type registration removed (QA Framework cleanup - entity types are defined in policy pack)

// Add RFP Agent plugin with filtered sidebar items for Keystone deployment
// TODO (Next Month - Feb 2026): Re-enable Executive Dashboard and Work Plan Execution features
// These features are temporarily hidden for the Keystone RFP deployment but remain available
// in the base rfp-agent plugin for other deployments.
const keystoneRfpPlugin = {
  ...rfpAgentPlugin,
  sidebarItems: rfpAgentPlugin.sidebarItems?.filter(item => {
    // Hide Executive Dashboard (path: /rfp/dashboard)
    if (item.path === '/rfp/dashboard') {
      console.log('[Keystone RFP] Hiding Executive Dashboard sidebar item (feature not yet ready)');
      return false;
    }
    // Work Plan & Execution is now enabled (path: /rfp/workplan-execution)
    return true;
  }) || [],
};

keystoneConfig.plugins = [keystoneRfpPlugin];

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App config={keystoneConfig} />
  </React.StrictMode>
);

